import { AlertFactory } from 'alerts/Alert'
import MemberStore from 'iam/MemberStore'
import Member from 'iam/models/Member'
import Workspace from 'iam/models/Workspace'
import SessionStore from 'iam/SessionStore'
import WorkspaceMembershipStore, {
  Membership,
} from 'iam/WorkspaceMembershipStore'
import WorkspaceStore from 'iam/WorkspaceStore'
import { Id } from 'types/definitions'
import { ProfileInfo, WorkspaceSignupStatePorts } from './WorkspaceSignupState'

export default class WorkspaceSignupStateAdapter
  implements WorkspaceSignupStatePorts
{
  constructor(
    private workspaceStore: WorkspaceStore,
    private sessionStore: SessionStore,
    private memberStore: MemberStore,
    private workspaceMembershipsStore: WorkspaceMembershipStore,
    public newAlert: AlertFactory,
    public checkMemberExists: (workspace: Workspace) => Promise<boolean>,
  ) {}

  fetchWorkspaceBySlug(slug: string): Promise<Workspace | null> {
    return this.workspaceStore.fetchWorkspace(slug)
  }

  fetchWorkspaceById(id: Id): Promise<Workspace | null> {
    return this.workspaceStore.fetch(id)
  }

  updateMember = async (member: Member, profileInfo: ProfileInfo) => {
    await this.memberStore.update(
      {
        workspace_id: Id.assert(member.workspace.id),
        user_public_id: Id.assert(member.user.publicId),
      },
      {
        given_name: profileInfo.given_name,
        family_name: profileInfo.family_name,
        phone: profileInfo.phone,
        email: profileInfo.email,
      },
    )
  }

  createMember = async (workspace: Workspace, profileInfo: ProfileInfo) => {
    const userId = Id.assert(this.sessionStore.user.id)
    const member = await this.memberStore.create({
      workspace_id: Id.assert(workspace.id),
      user_id: userId,
      ...profileInfo,
    })

    // If a member was created, then member-only info for a workspace has not been
    // returned, so clear the fetch cache for the workspace so the next fetch will
    // hit the API.
    this.workspaceStore.clearFetchCacheFor(Id.assert(workspace.id))
    this.workspaceMembershipsStore.addMembership({
      workspace,
      member,
    })
  }

  fetchMemberFromSession(workspaceId: Id): Promise<Member | null> {
    const workspaceIdString = Id.assert(workspaceId)
    const userId = Id.assert(this.sessionStore.user.id)
    return this.memberStore.fetch({
      workspace_id: workspaceIdString,
      user_id: userId,
      user_public_id: this.sessionStore.user.publicId,
    })
  }

  getMemberships(): Membership[] {
    return this.workspaceMembershipsStore.memberships
  }

  getAvailableProfileInfo(): Partial<ProfileInfo> {
    const user = this.sessionStore.user
    return {
      email: user.email,
      given_name: user.givenName,
      family_name: user.familyName,
      phone: user.phone,
    }
  }

  reportWorkspaceSelected(workspace: Workspace): Promise<void> {
    return this.sessionStore.selectWorkspace(workspace)
  }
}
